import Raven from 'raven-js';
import { useEffect, useMemo, useRef, useState } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Button } from 'foundations-components/transitional/components/Button';
import { LoadingButton } from 'foundations-components/transitional/components/LoadingButton';
import { useIntersectionObserver } from '../../../hooks/useIntersectionObserver';
import { usePurchaseMotionForAssignability } from '../../../hooks/usePurchaseMotionForAssignability';
import { trackPurchaseMotionFailure } from '../../../utils/metrics';
import { potentiallyOverridenAssignability } from '../../../utils/talkToSales';
import { getTrackingProperties, track } from '../../../utils/usageTracker';
import { ErrorBoundary, ErrorBoundaryNames } from '../../ErrorBoundary';
import CommModalContainer from './CommModalContainer';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const TalkToSalesButtonBase = props => {
  const {
    apiName,
    children,
    isAssignable: originalAssignability,
    level,
    trackingData,
    upgradeConfig,
    uiButtonProps,
    onCancel,
    onClick,
    'data-test-id': dataTestId,
    'data-selenium-id': dataSeleniumId
  } = props;
  const [showModal, setShowModal] = useState(false);
  const isAssignable = useMemo(() => potentiallyOverridenAssignability(originalAssignability), [originalAssignability]);
  const trackingProperties = useMemo(() => getTrackingProperties({
    apiName,
    trackingData,
    upgradeConfig,
    sourceKey: 'TALK_TO_SALES'
  }), [apiName, trackingData, upgradeConfig]);
  const {
    error,
    loading,
    purchaseMotion
  } = usePurchaseMotionForAssignability({
    isAssignable,
    apiName
  });
  const handleClick = () => {
    if (purchaseMotion) {
      onClick === null || onClick === void 0 || onClick(purchaseMotion);
      setShowModal(true);
    }
  };
  const handleClose = () => {
    setShowModal(false);
    onCancel === null || onCancel === void 0 || onCancel();
  };
  const internalRef = useRef(null);
  const buttonRef = (uiButtonProps === null || uiButtonProps === void 0 ? void 0 : uiButtonProps.buttonRef) || internalRef;
  useIntersectionObserver({
    targetRef: !loading ? buttonRef : undefined,
    onIntersect: () => {
      if (!loading) {
        track('contact-sales-button', Object.assign({
          action: 'viewed'
        }, trackingProperties));
        if (upgradeConfig) {
          track('interaction', Object.assign({
            action: 'viewed talk to sales',
            isAssignable: purchaseMotion === 'TALK_TO_SALES',
            isPrimaryCta: level === 0,
            isSecondaryCta: level === 1
          }, trackingProperties));
        }
      }
    }
  });
  useEffect(() => {
    if (error) {
      trackPurchaseMotionFailure({
        uniqueId: trackingData.uniqueId,
        apiName,
        purchaseMotion: 'TALK_TO_SALES'
      });
      Raven.captureException(error, {
        fingerprint: ['Failed to load TALK_TO_SALES_BUTTON purchase motion'],
        extra: {
          upgradeConfig
        }
      });
    }
  }, [apiName, error, trackingData.uniqueId, upgradeConfig]);

  // A pp redirect motion will only be returned for users in Russia
  if (purchaseMotion === 'BLOCKED') {
    return null;
  }
  if (loading) {
    return /*#__PURE__*/_jsx(LoadingButton, Object.assign({}, uiButtonProps, {
      loading: loading,
      children: children !== null && children !== void 0 ? children : /*#__PURE__*/_jsx(FormattedMessage, {
        message: "purchaseMotions.buttons.talkToSales"
      })
    }));
  }
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [showModal && /*#__PURE__*/_jsx(CommModalContainer, {
      apiName: apiName,
      trackingData: trackingData,
      upgradeConfig: upgradeConfig,
      level: level,
      isAssignable: purchaseMotion === 'TALK_TO_SALES',
      onClose: handleClose
    }), /*#__PURE__*/_jsx(Button, Object.assign({
      truncate: true,
      truncateStringProps: {
        justifyContent: 'center'
      }
    }, uiButtonProps, {
      buttonRef: buttonRef,
      onClick: handleClick,
      "data-test-id": dataTestId,
      "data-selenium-id": dataSeleniumId,
      children: children !== null && children !== void 0 ? children : /*#__PURE__*/_jsx(FormattedMessage, {
        message: "purchaseMotions.buttons.talkToSales"
      })
    }))]
  });
};
export const TalkToSalesButton = props => /*#__PURE__*/_jsx(ErrorBoundary, {
  boundaryName: ErrorBoundaryNames.TALK_TO_SALES_BUTTON,
  children: /*#__PURE__*/_jsx(TalkToSalesButtonBase, Object.assign({}, props))
});